/**
 * Tuning utility functions shared between components.
 * Extracted from HexKeyboardView for reuse in ZonesPanel.
 */

import type { TuningTable } from '@/types/config';

/**
 * Convert a cent-based interval to integer degree steps.
 * Uses round(interval * N / period) for tuning-agnostic quantization.
 * For EDO tunings this gives exact results; for non-EDO it gives the
 * best integer approximation, preventing duplicate pitches on adjacent keys.
 */
export function deriveDegreeStep(intervalCents: number, tuningTable: TuningTable): number {
  const N = tuningTable.notes_per_period;
  const period = tuningTable.period_cents;
  if (N === 0 || period === 0) return 0;
  return Math.round(intervalCents * N / period);
}

/**
 * Convert integer degree to 0.1-cent offset using tuning table pitches.
 * Handles negative degrees correctly via floor division.
 */
export function degreeToCents(degree: number, tuningTable: TuningTable): number {
  const N = tuningTable.notes_per_period;
  const period = tuningTable.period_cents;
  const periods = Math.floor(degree / N);
  const degInPeriod = ((degree % N) + N) % N;
  return periods * period + (tuningTable.pitches[degInPeriod] ?? 0);
}
